import React, { useMemo } from 'react'

import type { AnalyticsPrecision } from '../../../../types'
import { calculateValuePercentageChange } from '../../../../utils/calculateValuePercentageChange'
import { AnalyticsChangeIndicator } from '../../../AnalyticsChangeIndicator/AnalyticsChangeIndicator'
import { AnalyticsTooltip } from '../../../AnalyticsTooltip/AnalyticsTooltip'
import type {
  Align,
  AnalyticsTableCellDecorator,
  AnalyticsTableCellType,
} from '../../AnalyticsTable.types'
import { AnalyticsTableCellValueFormat } from '../AnalyticsTableCellValueFormat/AnalyticsTableCellValueFormat'
import * as S from './AnalyticsTableCell.styled'
import type { AnalyticsTableCellValue, BreakdownValue } from './AnalyticsTableCell.types'
import { isBreakdownValue, isValuePair } from './AnalyticsTableCell.types'
import { TableCellBreakdown } from './components/TableCellBreakdown'

type AnalyticsTableCellProps = {
  value: AnalyticsTableCellValue
  type: AnalyticsTableCellType
  decorator?: AnalyticsTableCellDecorator
  align?: Align
  columnIndex: number
  precision: AnalyticsPrecision
  isSummary?: boolean
  isNested?: boolean
  nestedLevel?: number
  metadata?: Record<string, string | number>
}

export const AnalyticsTableCell = ({
  value,
  type,
  decorator,
  align = 'left',
  columnIndex,
  precision,
  isSummary = false,
  isNested = false,
  nestedLevel = 0,
  metadata,
}: AnalyticsTableCellProps) => {
  const mainValue = useMemo(() => {
    const val = isValuePair(value) ? value[0] : value

    return isBreakdownValue(val) ? val.value : val
  }, [value])

  const subValue = useMemo(() => {
    if (!isValuePair(value)) return undefined
    const val = value[1]

    return isBreakdownValue(val) ? val.value : val
  }, [value])

  const renderMainValue = () => {
    const val = isValuePair(value) ? value[0] : value
    const hasBreakdown = isBreakdownValue(val) && val.breakdown?.items?.length > 0

    const displayedValue = (
      <S.MainValue bold={isSummary} underline={hasBreakdown}>
        <AnalyticsTableCellValueFormat
          value={mainValue}
          type={type}
          decorator={decorator}
          precision={precision}
          metadata={metadata}
          isSummary={isSummary}
          columnIndex={columnIndex}
        />
      </S.MainValue>
    )

    if (hasBreakdown) {
      return (
        <AnalyticsTooltip
          content={
            <TableCellBreakdown
              title={val.breakdown.title}
              items={val.breakdown.items}
              type={type}
            />
          }
        >
          {displayedValue}
        </AnalyticsTooltip>
      )
    }

    return displayedValue
  }

  const renderCompareValue = () => {
    if (!isValuePair(value)) return null
    const compareValue = value[1]
    const hasBreakdown = isBreakdownValue(value[1])

    const displayedValue = (
      <S.CompareValue underline={hasBreakdown}>
        <AnalyticsTableCellValueFormat
          value={subValue}
          type={type}
          decorator={decorator}
          precision={precision}
          metadata={metadata}
          isSummary={isSummary}
          columnIndex={columnIndex}
        />
      </S.CompareValue>
    )

    if (hasBreakdown) {
      return (
        <AnalyticsTooltip
          content={
            <TableCellBreakdown
              title={(compareValue as BreakdownValue).breakdown.title}
              items={(compareValue as BreakdownValue).breakdown.items}
              type={type}
            />
          }
        >
          {displayedValue}
        </AnalyticsTooltip>
      )
    }

    return displayedValue
  }

  const renderSummaryChanges = () => {
    if (columnIndex === 0) {
      return <S.ChangeIndicator align={align}>% Change</S.ChangeIndicator>
    }

    const isComparable = typeof mainValue === 'number' && typeof subValue === 'number'

    if (!isComparable)
      return (
        <S.ChangeIndicator align={align}>
          <AnalyticsChangeIndicator size="small" value={0} showValue={false} />
        </S.ChangeIndicator>
      )

    const changeValue = calculateValuePercentageChange([mainValue, subValue])

    return (
      <S.ChangeIndicator align={align}>
        <AnalyticsChangeIndicator size="small" value={changeValue} showZero={false} />
      </S.ChangeIndicator>
    )
  }

  return (
    <S.Container isNested={isNested} nestedLevel={nestedLevel}>
      {renderMainValue()}
      {renderCompareValue()}
      {isSummary && subValue && renderSummaryChanges()}
    </S.Container>
  )
}
